import type { SxProps, Theme } from '@mui/material';
import { DesktopDateTimePicker } from '@mui/x-date-pickers';
import type { DesktopDateTimePickerProps } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { DateTime } from 'luxon';
import { type FC, type FocusEventHandler, type ReactNode, type Ref, useState } from 'react';

type MUIDateTimePickerProps = DesktopDateTimePickerProps<DateTime>;

type DateTimePickerProps = Omit<
  MUIDateTimePickerProps,
  'value' | 'onChange' | 'onBlur' | 'minDateTime' | 'maxDateTime' | 'name'
> & {
  id?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  minDateTime?: string;
  maxDateTime?: string;
  minutesStep?: number;
  error?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  placeholder?: string;
  name?: string;
  label?: ReactNode;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  inputRef?: Ref<HTMLInputElement>;
};

export const DateTimePicker: FC<DateTimePickerProps> = (props) => {
  const onChange = (value: DateTime | null): void => {
    if (!value || !value.isValid) {
      props.onChange?.('');
    } else {
      props.onChange?.(value.toFormat("yyyy-MM-dd'T'HH:mm"));
    }
  };

  const {
    id,
    onBlur,
    error,
    fullWidth,
    helperText,
    placeholder,
    minutesStep = 1,
    readOnly,
    disabled,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const isEditable = readOnly !== true && disabled !== true;

  return (
    <DesktopDateTimePicker<DateTime>
      {...props}
      value={props.value ? DateTime.fromISO(props.value) : null}
      onChange={onChange}
      open={isOpen}
      onOpen={(): void => setIsOpen(true)}
      onClose={(): void => setIsOpen(false)}
      minDateTime={props.minDateTime ? DateTime.fromISO(props.minDateTime) : undefined}
      maxDateTime={props.maxDateTime ? DateTime.fromISO(props.maxDateTime) : undefined}
      views={['year', 'month', 'day', 'hours', 'minutes']}
      closeOnSelect={false}
      minutesStep={minutesStep}
      timeSteps={{ minutes: minutesStep }}
      slotProps={{
        textField: {
          id,
          onBlur,
          fullWidth,
          error,
          helperText,
          placeholder,
          onClick: (): void => {
            if (isEditable) {
              setIsOpen(true);
            }
          },
        },
        actionBar: {
          actions: ['clear', 'accept'],
        },
      }}
    />
  );
};
